<template>
  <v-container fluid>
    <h5 class="grey--text">{{ $t('diagnostics.title') }}</h5>
    <v-row>
      <v-col md="12">
        <h2 class="deep-purple--text lighten-3">{{ $t('diagnostics.all_diagnostics') }}</h2>
      </v-col>
    </v-row>

    <diagnostics-filter
      v-model="searchParams"
      :loading="loading"
      @search="search"
      @reset="reset"
    />

    <diagnostics-data-table
      :loading="loading"
      :diagnostics="diagnostics"
      :selected-country="searchParams.country || 'GENERAL'"
      v-model="page"
      :length="totalPages"
    />
  </v-container>
</template>

<script>
import diagnosticService from '@/services/diagnostic.service';
import DiagnosticsFilter from '@/components/diagnostics/DiagnosticsFilter.vue';
import DiagnosticsDataTable from '@/components/diagnostics/DiagnosticsDataTable.vue';
import snackbarMixin from '@/mixins/snackbar';
import { canAccessDiagnosticCustomization } from '@/utils/diagnosticPermissions';

export default {
  name: 'Diagnostics',
  components: {
    DiagnosticsFilter,
    DiagnosticsDataTable,
  },
  mixins: [snackbarMixin],
  data: () => ({
    loading: false,
    page: 1,
    pageSize: 15,
    totalPages: 1,
    diagnostics: [],
    searchParams: {
      country: 'GENERAL',
      grade: null,
      level: null,
      variant: null,
      search: '',
    },
  }),
  computed: {
    isAllowedUser() {
      return canAccessDiagnosticCustomization(this.$store.getters.currentUserEmail);
    },
  },
  async mounted() {
    if (!this.isAllowedUser) {
      this.snackbarError(this.$t('diagnostics.no_permission'));
      this.$router.push('/dashboard/colleges');
      return;
    }
    await this.loadDiagnostics();
  },
  watch: {
    page(newPage, previousPage) {
      if (newPage !== previousPage) {
        this.loadDiagnostics();
      }
    },
  },
  methods: {
    getDefaultSearchParams() {
      return {
        country: 'GENERAL',
        grade: null,
        level: null,
        variant: null,
        search: '',
      };
    },
    buildQueryParams() {
      const params = {
        page: this.page - 1,
        size: this.pageSize,
        excludeInitial: false,
      };

      if (this.searchParams.grade) {
        params.grade = this.searchParams.grade;
      }

      if (this.searchParams.level) {
        params.level = this.searchParams.level;
      }

      if (this.searchParams.search && this.searchParams.search.trim()) {
        params.search = this.searchParams.search.trim();
      }

      if (this.searchParams.country && this.searchParams.country !== 'GENERAL') {
        params.variantCountryCode = this.searchParams.country;
        params.hasVariant = this.searchParams.variant
          ? this.searchParams.variant === 'WITH'
          : true;
      } else if (this.searchParams.variant) {
        params.hasVariant = this.searchParams.variant === 'WITH';
      }

      return params;
    },
    async loadDiagnostics() {
      this.loading = true;
      try {
        const { data } = await diagnosticService.getDiagnosticsPage(this.buildQueryParams());
        // API wraps the response: data.content = Spring Page { content: [], totalPages }
        const page = data?.content || {};
        this.diagnostics = Array.isArray(page.content) ? page.content : [];
        this.totalPages = page.totalPages || 1;
      } catch (_error) {
        this.snackbarError(this.$t('general.error_msg'));
      } finally {
        this.loading = false;
      }
    },

    async search(filters) {
      this.searchParams = { ...filters };

      if (this.page === 1) {
        await this.loadDiagnostics();
      } else {
        this.page = 1;
      }
    },

    async reset() {
      this.searchParams = this.getDefaultSearchParams();

      if (this.page === 1) {
        await this.loadDiagnostics();
      } else {
        this.page = 1;
      }
    },
  },
};
</script>

<style scoped>
.grey--text {
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
</style>
